import React, { useMemo } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Paper,
  Grid,
} from '@material-ui/core';
import styled from 'styled-components';
import MoneyIcon from '@material-ui/icons/AttachMoney';
import moment from 'moment';
import tourPricingService from '../../services/tourPricingService';

// Función estándar para calcular duración
const duration = (date1, date2, forEffectives = false) => {
  let days = moment.utc(date2).diff(date1, "days");
  if(forEffectives){
    days += 1
  }
  return days || 0;
};

const StyledCard = styled(Card)`
  margin-bottom: 16px;
  
  .MuiCardHeader-root {
    background-color: #f5f5f5;
    padding: 12px 16px;
  }
  
  .MuiCardHeader-content {
    display: flex;
    align-items: center;
  }
  
  .MuiCardHeader-avatar {
    margin-right: 8px;
  }
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
    font-weight: bold;
    font-size: 1.1em;
  }
`;

const PaymentInfo = styled.div`
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  margin-top: 16px;
`;

/**
 * Componente para mostrar el resumen de costos de alquiler con cálculos corregidos
 * Basado en la tercera imagen del Rental Cost
 */
const RentalCostSummary = ({ tourData, pricing, showCorrections = true }) => {
  // Calcular días efectivos correctos
  const effectiveDays = useMemo(() => {
    if (!tourData || !tourData.rental_info) return 0;
    
    return duration(
      tourData.rental_info.effective_start_date, 
      tourData.rental_info.effective_end_date, 
      true
    );
  }, [tourData]);

  // Calcular precios corregidos
  const correctedPricing = useMemo(() => {
    if (!pricing || !pricing.breakdown) return null;

    const breakdown = pricing.breakdown;
    const corrections = {};

    // Corregir GPS si es necesario
    if (breakdown.gpsPrice > 0 && effectiveDays !== breakdown.effectiveDays) {
      const dailyGpsRate = breakdown.gpsPrice / breakdown.effectiveDays;
      corrections.gps = {
        original: breakdown.gpsPrice,
        corrected: dailyGpsRate * effectiveDays,
        dailyRate: dailyGpsRate,
        days: effectiveDays
      };
    }

    return corrections;
  }, [pricing, effectiveDays]);

  // Calcular totales usando la lógica exacta del backend
  const totals = useMemo(() => {
    const exactTotals = tourPricingService.calculateTotalsExact({ tourData, pricing });
    
    return {
      basePrice: exactTotals.breakdown.tourPrice1 || 0,
      passengerPrice: exactTotals.breakdown.passengerPrice || 0,
      gpsPrice: exactTotals.breakdown.gpsPrice || 0,
      total: exactTotals.total1 || 0,
      total1: exactTotals.total1 || 0,
      total2: exactTotals.total2 || 0,
      breakdown: exactTotals.breakdown
    };
  }, [tourData, pricing]);

  // Formatear moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  if (!tourData) {
    return (
      <StyledCard>
        <CardContent>
          <Typography variant="body2" color="textSecondary">
            No hay datos del tour disponibles
          </Typography>
        </CardContent>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <CardHeader>
        <MoneyIcon />
        <Typography variant="h6">
          Rental Cost
        </Typography>
      </CardHeader>
      
      <CardContent>
        {/* Tour Base Price */}
        <InfoRow>
          <Typography variant="body2">
            Tour Base Price
          </Typography>
          <Typography variant="body2">
            {formatCurrency(totals.basePrice)} (1st) / {formatCurrency(totals.basePrice)} (2nd)
          </Typography>
        </InfoRow>

        {/* Passenger Price */}
        <InfoRow>
          <Typography variant="body2">
            Passenger Price
          </Typography>
          <Typography variant="body2" style={{ color: '#ff9800' }}>
            {formatCurrency(totals.passengerPrice)}
          </Typography>
        </InfoRow>

        {/* GPS Device */}
        <InfoRow>
          <Typography variant="body2">
            GPS Device
          </Typography>
          <Typography variant="body2">
            {correctedPricing && correctedPricing.gps && showCorrections ? (
              `${formatCurrency(correctedPricing.gps.dailyRate)} × ${correctedPricing.gps.days} days = ${formatCurrency(correctedPricing.gps.corrected)}`
            ) : (
              `${formatCurrency(10)} × ${effectiveDays} days = ${formatCurrency(10 * effectiveDays)}`
            )}
          </Typography>
        </InfoRow>

        {/* Total Prices */}
        <InfoRow>
          <Typography variant="body2">
            Total Price (1st Choice)
          </Typography>
          <Typography variant="body2">
            {formatCurrency(totals.total1)}
          </Typography>
        </InfoRow>

        <InfoRow>
          <Typography variant="body2">
            Total Price (2nd Choice)
          </Typography>
          <Typography variant="body2">
            {formatCurrency(totals.total2)}
          </Typography>
        </InfoRow>

        {/* Credit Card Info */}
        <PaymentInfo>
          <InfoRow>
            <Typography variant="body2">
              Credit Card
            </Typography>
            <Typography variant="body2">
              {tourData.clientData.card_type + " - " + tourData.clientData.card_number + " - " + tourData.clientData.card_expiration + " - " + tourData.clientData.card_cvv}
            </Typography>
          </InfoRow>
        </PaymentInfo>

        {/* Mostrar correcciones si están habilitadas */}
       {/*  {showCorrections && correctedPricing && Object.keys(correctedPricing).length > 0 && (
          <Paper style={{ marginTop: 16, padding: 16, backgroundColor: '#fff3e0' }}>
            <Typography variant="subtitle2" style={{ color: '#f57c00', marginBottom: 8 }}>
              Correcciones Aplicadas:
            </Typography>
            
            {correctedPricing.gps && (
              <Typography variant="body2" style={{ marginBottom: 4 }}>
                • GPS Device: {formatCurrency(correctedPricing.gps.original)} → {formatCurrency(correctedPricing.gps.corrected)}
                {" "}({correctedPricing.gps.days} días efectivos en lugar de {(pricing && pricing.breakdown && pricing.breakdown.effectiveDays) || 0})
              </Typography>
            )}
            
            <Typography variant="body2" style={{ marginTop: 8, fontWeight: 'bold' }}>
              Total corregido: {formatCurrency(totals.total)}
            </Typography>
          </Paper>
        )} */}
      </CardContent>
    </StyledCard>
  );
};

export default RentalCostSummary; 


// WEBPACK FOOTER //
// ./src/components/tour/RentalCostSummary.js